import { useQuery } from '@tanstack/react-query';
import { GIFT_CARD_ISSUANCE_ENABLED_LIMIT_KEY } from '@heyroot/contracts/gift-cards/issuance-switch';
import { fetchRegulatedProfile, queryKeys, regulatedGuardsApply } from './api';

export type IssuanceStop = {
  /** The select value and the badge text this row shows in place of its access mode. */
  value: string;
  label: string;
  /** Re-selecting full would only set the issuance switch, which the reader ignores for a law firm. */
  blocksFull: boolean;
};

type IssuanceRow = { moduleKey: string; accessMode: string; limits?: Record<string, unknown> };

/**
 * The stop state of an entitlement row: a gift-cards row on full whose issuance is off, or null.
 *
 * A tenant HeyRoot recorded as a law firm (or whose record cannot be read) has gift-card issuance
 * read OFF by the root-api reader whatever the saved switch says (J12b, R-24), so the console says
 * that instead of "on" (#16377). The profile wins over the saved switch for that reason. It reads
 * the same query key and function as RegulatedProfileSection, so react-query shares one request.
 * Until it loads, or if this operator cannot read it, the console claims nothing about the profile.
 */
export function useIssuanceStopFor(tenantId: string): (row: IssuanceRow) => IssuanceStop | null {
  const { data } = useQuery({
    queryKey: queryKeys.regulatedProfile(tenantId),
    queryFn: () => fetchRegulatedProfile(tenantId),
    retry: false,
  });
  const regulatedOff = regulatedGuardsApply(data);
  return (row) => {
    if (row.moduleKey !== 'gift-cards' || row.accessMode !== 'full') return null;
    if (regulatedOff) {
      return {
        value: 'issuance-off-by-profile',
        label: 'full · issuance off, regulated profile',
        blocksFull: true,
      };
    }
    if (row.limits?.[GIFT_CARD_ISSUANCE_ENABLED_LIMIT_KEY] !== 1) {
      return { value: 'issuance-disabled', label: 'full · issuance disabled', blocksFull: false };
    }
    return null;
  };
}
